import React, { useMemo } from 'react';
import { epdSelectStyles, FixedLabel, WizardStep1Row } from '../styles';
import { ActionState, EPDModel, VerificationType } from 'services/EpdClient';
import { useTranslation } from 'react-i18next';
import HelpIcon from 'components/icons/HelpIcon';
import { Validation } from '../Validation';
import { WizardStepValidationSchema } from '../types';
import EpdSelectComponent from 'components/form/EpdSelectComponent';
import { SingleValue } from 'react-select';
import filterToolsForEpdVerificationIntoOptions from 'util/filterToolsForEpdVerificationIntoOptions';
import { Option } from 'types/types';
import { Input } from 'styles/Styles.styled';
import { useCompanyTools, useCompanyToolVersions } from 'services/api/toolsQueries';

interface Props {
  epd: EPDModel;
  validation: WizardStepValidationSchema;
  onChangeEpd(propertyName: string, value: any): void;
}

const WizardStep1Tools: React.FC<Props> = ({ epd, validation, onChangeEpd }) => {
  const { t } = useTranslation();

  const companyId = epd.epdOwner?.companyId;
  const tools = useCompanyTools(companyId).data || [];
  const toolVersions = useCompanyToolVersions(companyId).data || [];

  const toolOptions = useMemo(
    () =>
      filterToolsForEpdVerificationIntoOptions(
        tools,
        toolVersions,
        epd.verificationType ?? VerificationType.EPDVerification,
        epd.epdOwner?.companyName || ''
      ),
    [tools, toolVersions, epd.verificationType, epd.epdOwner?.companyName]
  );

  const changePreverifiedTool = (tool: SingleValue<Option>) => {
    if (tool?.value) {
      onChangeEpd('preverifiedTool', tool.value);
    } else {
      onChangeEpd('preverifiedTool', undefined);
    }
  };

  const changePreverifiedToolVersion = (toolVersion: SingleValue<Option>) => {
    if (toolVersion?.value) {
      onChangeEpd('preverifiedToolVersion', toolVersion.value);
    } else {
      onChangeEpd('preverifiedToolVersion', undefined);
    }
  };

  const selectedPreverifiedTool =
    epd.preverifiedTool && ({ label: epd.preverifiedTool.name, value: epd.preverifiedTool.id } as Option);

  const selectedPreverifiedToolVersion =
    epd.preverifiedToolVersion &&
    ({ label: epd.preverifiedToolVersion.name, value: epd.preverifiedToolVersion.id } as Option);

  return (
    <>
      <WizardStep1Row state={epd.preverifiedToolState.actionState}>
        <FixedLabel>
          {t('epdWizard.wizardStep1.properties.preVerifiedTool.title')}
          <HelpIcon content={t('epdWizard.wizardStep1.tooltips.preVerifiedTool')} />{' '}
          <Validation validation={validation} field="preverifiedTool" epd={epd} />
        </FixedLabel>
        <EpdSelectComponent
          placeholder={t('placeholder.defaultSelect')}
          isDisabled={epd.preverifiedToolState?.actionState === ActionState.Disabled}
          onChange={(e: SingleValue<Option>) => changePreverifiedTool(e)}
          value={selectedPreverifiedTool}
          isMulti={false}
          styles={epdSelectStyles}
          isClearable={true}
          options={toolOptions}
        />
      </WizardStep1Row>
      <WizardStep1Row state={epd.preverifiedToolState.actionState}>
        <FixedLabel>
          Tool Version
          <Validation validation={validation} field="preverifiedToolVersion" epd={epd} />
        </FixedLabel>
        <EpdSelectComponent
          placeholder={t('placeholder.defaultSelect')}
          isDisabled={epd.preverifiedToolState?.actionState === ActionState.Disabled || !epd.preverifiedTool}
          onChange={(e: SingleValue<Option>) => changePreverifiedToolVersion(e)}
          value={selectedPreverifiedToolVersion}
          isMulti={false}
          styles={epdSelectStyles}
          isClearable={true}
          options={toolOptions.find((o) => o.value === selectedPreverifiedTool?.value)?.versions}
        />
      </WizardStep1Row>
      <WizardStep1Row state={epd.preverifiedToolState.actionState}>
        <FixedLabel>Tool verifier</FixedLabel>
        <Input
          disabled
          name="verification"
          value={toolVersions.find((x) => x.id === selectedPreverifiedToolVersion?.value)?.verifierName}
        />
      </WizardStep1Row>
    </>
  );
};

export default WizardStep1Tools;
